/** Typed client for the dedicated POS REST API. POS deliberately does not reuse
 * the ship-fit action endpoints: plans have separate ownership and revision rules. */
import type { PosCalculation, PosCatalogStructure, PosPlanSnapshot, PosServerPlan, PosTower } from '../../shared/pos';

export class PosApiError extends Error {
  constructor(message: string, public readonly status: number, public readonly details?: unknown) {
    super(message);
    this.name = 'PosApiError';
  }
}

async function readBody(response: Response): Promise<unknown> {
  const text = await response.text();
  if (!text) return null;
  try { return JSON.parse(text) as unknown; }
  catch { return text; }
}

async function request<T>(path: string, options?: RequestInit): Promise<T> {
  const response = await fetch(`/api/pos${path}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    ...options,
  });
  const body = await readBody(response);
  if (!response.ok) {
    const message = body && typeof body === 'object' && 'error' in body
      ? String(body.error) : `POS API request failed (${response.status})`;
    const details = body && typeof body === 'object' && 'details' in body ? body.details : undefined;
    throw new PosApiError(message, response.status, details);
  }
  if (body && typeof body === 'object' && 'success' in body && 'data' in body) {
    if (!body.success) throw new PosApiError('error' in body ? String(body.error) : 'POS API request failed', response.status);
    return body.data as T;
  }
  return body as T;
}

function query(params: Record<string, string | number | boolean | undefined>): string {
  const entries = Object.entries(params).filter(([, value]) => value !== undefined && value !== '');
  return entries.length
    ? `?${entries.map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`).join('&')}`
    : '';
}

export const fetchPosRules = () => request<{ version?: string; rulesVersion?: string }>('/rules');
export const fetchPosTowers = (q?: string) => request<PosTower[]>(`/catalog/towers${query({ q })}`);
export const fetchPosStructures = (filters: { search?: string; groupID?: number; role?: string; lifecycle?: string; includeUnsupported?: boolean }) =>
  request<PosCatalogStructure[]>(`/catalog/structures${query({ q: filters.search, groupID: filters.groupID, role: filters.role, lifecycle: filters.lifecycle, includeUnsupported: filters.includeUnsupported, limit: 250 })}`);
export const fetchPosStructureDetail = (typeID: number) => request<PosCatalogStructure>(`/catalog/structures/${typeID}`);
export const calculatePos = (plan: PosPlanSnapshot) => request<PosCalculation>('/calculate', { method: 'POST', body: JSON.stringify({ plan }) });
export const listPosPlans = () => request<PosServerPlan[]>('/plans');
export const createPosPlan = (plan: PosPlanSnapshot) => request<PosServerPlan>('/plans', { method: 'POST', body: JSON.stringify(plan) });
export const readPosPlan = (id: number) => request<PosServerPlan>(`/plans/${id}`);
export const updatePosPlan = (id: number, plan: PosPlanSnapshot) => request<PosServerPlan>(`/plans/${id}`, { method: 'PUT', body: JSON.stringify(plan) });
export const replacePosStructures = (id: number, revision: number, structures: PosPlanSnapshot['structures']) =>
  request<PosServerPlan>(`/plans/${id}/structures/batch`, { method: 'POST', body: JSON.stringify({ revision, structures }) });
export const deletePosPlan = (id: number) => request<void>(`/plans/${id}`, { method: 'DELETE' });
export const readPosShare = (shareId: string) => request<PosServerPlan>(`/shares/${encodeURIComponent(shareId)}`);
export const duplicatePosPlan = (id: number) => request<PosServerPlan>(`/plans/${id}/duplicate`, { method: 'POST' });
export const duplicatePosShare = (shareId: string) => request<PosServerPlan>(`/shares/${encodeURIComponent(shareId)}/duplicate`, { method: 'POST' });
export const createPosShare = (id: number, revision: number) => request<{ shareId: string; url?: string; revision: number; plan: PosServerPlan }>(`/plans/${id}/share`, { method: 'PUT', body: JSON.stringify({ revision }) });
export const revokePosShare = (id: number, revision: number) => request<PosServerPlan>(`/plans/${id}/share`, { method: 'DELETE', body: JSON.stringify({ revision }) });
export const exportPosPlan = (id: number, format: 'json' | 'csv' | 'multibuy' | 'checklist' = 'json') =>
  request<unknown>(`/plans/${id}/export${query({ format })}`);
export const validatePosImport = (document: unknown) => request<{ valid: boolean; errors?: string[]; issues?: Array<{ path: string; message: string }> }>('/imports/validate', { method: 'POST', body: JSON.stringify({ document }) });
export const importPosDocument = (document: unknown) => request<PosServerPlan>('/imports', { method: 'POST', body: JSON.stringify({ document }) });
